import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { ClipboardList } from 'lucide-react'
import {
  Card,
  EmptyState,
  ErrorState,
  LoadMore,
  SegmentedControl,
  Skeleton,
} from '@shared/design-system/ui'
import { useMediaQuery } from '@shared/lib/use-media-query'
import { useAdminKycActionRequiredInfinite } from '../api/admin.api'
import type { AdminKycActionRequired, AdminKycQueue } from '../api/admin.api'
import { formatAdminDate, formatAdminDateTime } from './kyc-support-format'
import { nextStepOwnerLabels, nextStepText } from './kyc-onboarding-labels'
import {
  date,
  empty,
  header,
  headerParagraph,
  mobileCard,
  mobileHeading,
  mobileList,
  mobileMeta,
  mobileMetaItem,
  mobileMetaLabel,
  mobileMetaValue,
  page,
  participant,
  participantLink,
  queueControl,
  table,
  tableCard,
  tableViewport,
  td,
  th,
  title,
  type,
} from './AdminActionRequiredScreen.css'

const queueOptions = [
  { value: 'support', label: 'Precisa de suporte' },
  { value: 'seller', label: 'Aguardando o vendedor' },
  { value: 'stripe_review', label: 'Em análise na Stripe (+3 dias)' },
] as const

const queueDateLabels: Readonly<Record<AdminKycQueue, string>> = {
  support: 'Desde',
  seller: 'Prazo',
  stripe_review: 'Em análise desde',
}

function ParticipantLink({ item }: { item: AdminKycActionRequired }) {
  return (
    <div className={participant}>
      <Link className={participantLink} to="/usuarios/$userId" params={{ userId: item.userId }}>
        {item.name?.trim() || item.email}
      </Link>
      <span>{item.email}</span>
    </div>
  )
}

function NextStep({ item, queue }: { item: AdminKycActionRequired; queue: AdminKycQueue }) {
  const step = item.onboarding.nextStep
  if (queue === 'stripe_review') {
    return (
      <span>
        <strong>
          Stripe · Em análise desde {formatAdminDateTime(step.since ?? item.date ?? '')}
        </strong>
        {step.owner === 'seller' ? (
          <small>
            <br />
            Vendedor também: {nextStepText(step)}
          </small>
        ) : null}
      </span>
    )
  }
  return (
    <span>
      <strong>{nextStepOwnerLabels[step.owner]}</strong>
      <br />
      {nextStepText(step)}
    </span>
  )
}

function QueueDate({ item }: { item: AdminKycActionRequired }) {
  return <span className={date}>{item.date ? formatAdminDate(item.date) : 'Sem data'}</span>
}

function QueueTable({ items, queue }: { items: AdminKycActionRequired[]; queue: AdminKycQueue }) {
  return (
    <div className={tableViewport}>
      <table className={table}>
        <thead>
          <tr>
            <th className={th} scope="col">
              Vendedor
            </th>
            <th className={th} scope="col">
              PF/PJ
            </th>
            <th className={th} scope="col">
              Próximo passo
            </th>
            <th className={th} scope="col">
              {queueDateLabels[queue]}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.userId}>
              <td className={td}>
                <ParticipantLink item={item} />
              </td>
              <td className={td}>
                <span className={type}>{item.businessType ?? 'Não informado'}</span>
              </td>
              <td className={td}>
                <NextStep item={item} queue={queue} />
              </td>
              <td className={td}>
                <QueueDate item={item} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function QueueCards({ items, queue }: { items: AdminKycActionRequired[]; queue: AdminKycQueue }) {
  return (
    <div className={mobileList}>
      {items.map((item) => (
        <article className={mobileCard} key={item.userId}>
          <div className={mobileHeading}>
            <ParticipantLink item={item} />
          </div>
          <span className={type}>{item.businessType ?? 'Não informado'}</span>
          <div className={mobileMeta}>
            <Meta label="Próximo passo">
              <NextStep item={item} queue={queue} />
            </Meta>
            <Meta label={queueDateLabels[queue]}>
              <QueueDate item={item} />
            </Meta>
          </div>
        </article>
      ))}
    </div>
  )
}

function Meta({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span className={mobileMetaItem}>
      <span className={mobileMetaLabel}>{label}</span>
      <span className={mobileMetaValue}>{children}</span>
    </span>
  )
}

export function AdminActionRequiredScreen() {
  const [queue, setQueue] = useState<AdminKycQueue>('support')
  const query = useAdminKycActionRequiredInfinite(queue)
  const isMobile = useMediaQuery('(max-width: 720px)')
  const items = useMemo(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data?.pages],
  )

  return (
    <section className={page} data-testid="action-required-screen">
      <header className={header}>
        <h1 className={title}>Cadastros pendentes</h1>
        <SegmentedControl
          value={queue}
          options={queueOptions}
          onChange={setQueue}
          ariaLabel="Fila de cadastros"
        />
        <p className={headerParagraph}>
          Veja quem precisa de suporte, de uma ação do vendedor ou de acompanhamento da Stripe.
        </p>
      </header>
      <div className={queueControl} aria-live="polite">
        {items.length} cadastro{items.length === 1 ? '' : 's'} carregado
        {items.length === 1 ? '' : 's'}
      </div>

      <Card padding="none" className={tableCard}>
        {query.isPending ? (
          <div className={empty}>
            <Skeleton height={280} />
          </div>
        ) : query.isError && (items.length === 0 || !query.isFetchNextPageError) ? (
          <div className={empty}>
            <ErrorState
              title="Não foi possível carregar a fila"
              onRetry={() => void query.refetch()}
            />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<ClipboardList size={24} />}
            title="Nenhum cadastro nesta fila agora."
          />
        ) : (
          <>
            {isMobile ? (
              <QueueCards items={items} queue={queue} />
            ) : (
              <QueueTable items={items} queue={queue} />
            )}
            <LoadMore
              shown={items.length}
              hasMore={query.hasNextPage}
              loading={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
              error={query.isFetchNextPageError}
              summaryLabel={(shown) => `Mostrando ${shown} cadastro${shown === 1 ? '' : 's'}`}
            />
          </>
        )}
      </Card>
    </section>
  )
}
